import { useMemo, useState, type ReactNode } from 'react'
import type { MetaPaginasi } from '@/lib/api'
import { Button } from './Button'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import { SkeletonTabel } from './Skeleton'

export interface KolomTabel<T> {
  kunci: string
  judul: ReactNode
  render?: (baris: T, indeks: number) => ReactNode
  sortable?: boolean
  nilaiSort?: (baris: T) => string | number
  rata?: 'kiri' | 'kanan' | 'tengah'
  lebar?: string
  kelas?: string
}

export interface PropsTabel<T> {
  kolom: KolomTabel<T>[]
  data: T[]
  kunciBaris: (baris: T, indeks: number) => string
  memuat?: boolean
  galat?: string | null
  kodeGalat?: string | null
  onCoba?: () => void
  meta?: MetaPaginasi | null
  onHalaman?: (halaman: number) => void
  onUrut?: (urut: { sortBy: string; sortDir: 'asc' | 'desc' }) => void
  urutSaatIni?: { sortBy?: string; sortDir: 'asc' | 'desc' }
  pesanKosong?: string
  deskripsiKosong?: ReactNode
  aksiKosong?: ReactNode
  onKlikBaris?: (baris: T) => void
  kelasBaris?: (baris: T) => string
  kelasPembungkus?: string
}

/**
 * Tabel data generik:
 *  - pengurutan di klien (bawaan) atau di server bila `onUrut` diberikan;
 *  - paginasi SERVER melalui prop `meta` + `onHalaman`;
 *  - state memuat / galat / kosong selalu terlihat.
 */
export function DataTable<T>({
  kolom,
  data,
  kunciBaris,
  memuat = false,
  galat = null,
  kodeGalat = null,
  onCoba,
  meta = null,
  onHalaman,
  onUrut,
  urutSaatIni,
  pesanKosong = 'Belum ada data untuk filter ini.',
  deskripsiKosong,
  aksiKosong,
  onKlikBaris,
  kelasBaris,
  kelasPembungkus = '',
}: PropsTabel<T>) {
  const [urutLokal, setUrutLokal] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' } | null>(null)

  const urutAktif = onUrut ? urutSaatIni ?? null : urutLokal

  const dataTampil = useMemo(() => {
    if (onUrut || !urutLokal) return data
    const k = kolom.find((c) => c.kunci === urutLokal.sortBy)
    const ambil = k?.nilaiSort ?? ((b: T) => nilaiDatar(b, urutLokal.sortBy))
    const faktor = urutLokal.sortDir === 'asc' ? 1 : -1
    return [...data].sort((a, b) => {
      const va = ambil(a)
      const vb = ambil(b)
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * faktor
      return String(va).localeCompare(String(vb), 'id-ID', { numeric: true }) * faktor
    })
  }, [data, kolom, urutLokal, onUrut])

  const ubahUrut = (kunci: string) => {
    const arahBaru: 'asc' | 'desc' =
      urutAktif && urutAktif.sortBy === kunci && urutAktif.sortDir === 'asc' ? 'desc' : 'asc'
    if (onUrut) onUrut({ sortBy: kunci, sortDir: arahBaru })
    else setUrutLokal({ sortBy: kunci, sortDir: arahBaru })
  }

  if (galat) {
    return (
      <div className={kelasPembungkus}>
        <ErrorState pesan={galat} kode={kodeGalat} onCoba={onCoba} />
      </div>
    )
  }

  if (memuat && data.length === 0) {
    return (
      <div className={`rounded-xl border border-netral-200 bg-white p-3 ${kelasPembungkus}`}>
        <SkeletonTabel kolom={Math.min(kolom.length, 6)} />
      </div>
    )
  }

  return (
    <div className={kelasPembungkus}>
      <div className="overflow-x-auto rounded-xl border border-netral-200 bg-white">
        <table className="w-full min-w-full text-sm">
          <thead className="bg-netral-50 text-left text-xs font-semibold tracking-wide text-netral-600 uppercase">
            <tr>
              {kolom.map((c) => {
                const bisaUrut = c.sortable !== false
                const aktif = urutAktif?.sortBy === c.kunci
                return (
                  <th
                    key={c.kunci}
                    scope="col"
                    style={c.lebar ? { width: c.lebar } : undefined}
                    className={`px-3 py-2.5 whitespace-nowrap ${rataKelas(c.rata)}`}
                  >
                    {bisaUrut ? (
                      <button
                        type="button"
                        onClick={() => ubahUrut(c.kunci)}
                        className="inline-flex items-center gap-1 hover:text-yayasan-800"
                        title="Klik untuk mengurutkan"
                      >
                        {c.judul}
                        <span aria-hidden="true" className={aktif ? 'text-yayasan-700' : 'text-netral-300'}>
                          {aktif ? (urutAktif?.sortDir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      c.judul
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-netral-100">
            {dataTampil.length === 0 ? (
              <tr>
                <td colSpan={kolom.length}>
                  <EmptyState judul={pesanKosong} deskripsi={deskripsiKosong} aksi={aksiKosong} />
                </td>
              </tr>
            ) : (
              dataTampil.map((baris, indeks) => (
                <tr
                  key={kunciBaris(baris, indeks)}
                  onClick={onKlikBaris ? () => onKlikBaris(baris) : undefined}
                  className={`${onKlikBaris ? 'cursor-pointer hover:bg-yayasan-50/60' : 'hover:bg-netral-50'} ${
                    kelasBaris ? kelasBaris(baris) : ''
                  }`}
                >
                  {kolom.map((c) => (
                    <td
                      key={c.kunci}
                      className={`px-3 py-2.5 align-top ${rataKelas(c.rata)} ${c.kelas ?? ''}`}
                    >
                      {c.render ? c.render(baris, indeks) : teks(nilaiDatar(baris, c.kunci))}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && onHalaman ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-netral-600">
          <p>
            Menampilkan{' '}
            <span className="font-medium text-netral-800">{data.length}</span> dari{' '}
            <span className="font-medium text-netral-800">{meta.total}</span> data
            {meta.totalPages > 1 ? ` (halaman ${meta.page} dari ${meta.totalPages})` : ''}
          </p>
          {meta.totalPages > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                varian="garis"
                ukuran="kecil"
                disabled={meta.page <= 1}
                onClick={() => onHalaman(meta.page - 1)}
              >
                Sebelumnya
              </Button>
              <Button
                varian="garis"
                ukuran="kecil"
                disabled={meta.page >= meta.totalPages}
                onClick={() => onHalaman(meta.page + 1)}
              >
                Berikutnya
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function rataKelas(rata?: 'kiri' | 'kanan' | 'tengah'): string {
  if (rata === 'kanan') return 'text-right'
  if (rata === 'tengah') return 'text-center'
  return 'text-left'
}

export function nilaiDatar(obyek: unknown, jalur: string): unknown {
  if (obyek === null || obyek === undefined) return undefined
  if (jalur === '') return obyek
  const bagian = jalur.split('.')
  let kini: unknown = obyek
  for (const b of bagian) {
    if (kini === null || kini === undefined) return undefined
    if (Array.isArray(kini)) {
      const i = Number(b)
      kini = Number.isInteger(i) ? kini[i] : undefined
    } else if (typeof kini === 'object') {
      kini = (kini as Record<string, unknown>)[b]
    } else {
      return undefined
    }
  }
  return kini
}

function teks(nilai: unknown): ReactNode {
  if (nilai === null || nilai === undefined || nilai === '') return <span className="text-netral-400">-</span>
  if (typeof nilai === 'boolean') return nilai ? 'Ya' : 'Tidak'
  if (typeof nilai === 'number') return nilai.toLocaleString('id-ID')
  if (typeof nilai === 'object') return JSON.stringify(nilai)
  return String(nilai)
}

export function HalamanRingkas({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-2 text-sm text-netral-600">{children}</div>
}
